import { NgClass, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  input,
  linkedSignal,
  output,
  signal,
  viewChild,
  TemplateRef,
} from '@angular/core';
import { StoreService } from '../../../store/store.service';
import { FADE, GROW } from '../../animations/animations';
import { SafeHtml } from '../../pipes/SafeHtml.pipe';
import { CommonService } from '../../services/common.service';
import { ShoppingCartService } from '../../services/shopping-cart.service';
import { ModalService } from '../../services/modal.service';
import { SidePanelService } from '../../services/side-panel.service';
import { ButtonComponent } from '../button/button.component';
import { CardComponent } from '../card/card.component';
import { CarouselComponent, CarouselConfig } from '../carousel/carousel.component';
import { RoomCardComponent } from '../room-card/room-card.component';
import { TagComponent } from '../tag/tag.component';
import { SkeletonLoaderItemComponent } from '../skeleton-loader-item/skeleton-loader-item';
import { SidePanelComponent } from '../side-panel/side-panel.component';

@Component({
  standalone: true,
  imports: [
    CardComponent,
    SafeHtml,
    NgClass,
    TagComponent,
    ButtonComponent,
    RoomCardComponent,
    SidePanelComponent,
    CarouselComponent,
    NgTemplateOutlet,
    SkeletonLoaderItemComponent,
  ],
  selector: 'app-rateplan-card',
  templateUrl: 'rateplan-card.component.html',
  styleUrl: 'rateplan-card.component.scss',
  host: {
    '[@grow]': 'true',
    '(click)': 'onCardClick($event)',
  },
  animations: [GROW, FADE],
})
export class RateplanCardComponent {
  rateplanDetailsContentTemplate = viewChild<TemplateRef<any>>('rateplanDetailsContent');
  rateplanRoomsListContentTemplate = viewChild<TemplateRef<any>>('rateplanRoomsListContent');

  // Inputs
  public readonly data = input.required<any>({});
  public readonly items = input.required<any[]>();
  public readonly index = input.required<number>();

  // Optional Inputs
  public readonly isExpanded = input<boolean>(false);
  public readonly isLoading = input<boolean>(false);
  public readonly isItemsLoading = input<boolean>(false);
  public readonly isDisabled = input<boolean>(false);
  public readonly selectedRoomCode = input<string>('');
  public readonly isAddToCartInProgress = input<boolean>(false);
  public readonly isSpecialRate = input<boolean>(false);
  public readonly isPromoRate = input<boolean>(false);

  // Outputs
  public readonly onStateChanged = output<{ index: number; state: 'EXPANDED' | 'COLLAPSED' } | null>();
  public readonly onRoomRateSelected = output<{
    room: any;
    rate: any;
    bedtype: { isDefault: boolean; code: string };
  }>();

  // Signals
  public readonly rateDetailsClickedWhileCollapsed = signal<boolean>(false);
  public readonly disableCard = linkedSignal<boolean>(() => this.isDisabled());

  // Computed Signals
  public readonly isMobileScreen = computed<boolean>(
    () => (this._storeSvc.res().mobile || this._storeSvc.res().tablet) ?? false,
  );
  public readonly rateplanImages = computed<{ src: string; alt: string }[]>(() => {
    const data = this.data();
    let images: { src: string; alt: any }[] = [];

    if (data?.imageUrls && Array.isArray(data.imageUrls) && data.imageUrls.length > 0) {
      images = data.imageUrls.map((imageUrl: any, index: number) => ({
        src: imageUrl.largeImageUrl || imageUrl.thumbnailImageUrl,
        alt: imageUrl.imageAltText || `Room Image ${index + 1}`,
      }));
    } else if (
      (this.isSpecialRate() || this.isPromoRate()) &&
      data?.imageInfo &&
      Array.isArray(data.imageInfo) &&
      data.imageInfo.length > 0
    ) {
      images = data.imageInfo.map((img: any, index: number) => ({
        src: img?.urls?.large || img?.urls?.thumbnail,
        alt: img?.alt_text || `Special Rate Image ${index + 1}`,
      }));
    }

    return images;
  });
  public readonly carouselConfig = computed(
    (): CarouselConfig => ({
      showNavigationArrowsOnHover: !this.isMobileScreen(),
      loop: true,
      autoPlay: true,
      interval: 3000,
    }),
  );
  public readonly showStrikeoutprice = computed<boolean>(
    () => this._storeSvc.propInfo()?.displayDprStrikethrough ?? false,
  );
  public readonly showDPRMessage = computed(() => this._storeSvc.propInfo()?.displayDprTagline ?? false);
  public readonly pricing = computed(() => {
    if (this.isLoading()) {
      return null;
    }

    const currency = this._storeSvc.userStore.currency();
    const rateplan = this.data();
    const localeObj = this._storeSvc.localeObj();
    const showPriceInclusiveOfFees = this._shoppingCartSvc.showTaxesInDisplayPrice();

    // Upsell Price
    const hasUpsellPrice = rateplan?.isUpsellRoom && !!rateplan?.upsellPriceInfo;
    const upsellPrice = hasUpsellPrice
      ? rateplan?.upsellPriceInfo?.averageUpsellPriceByCurrency[currency.code] ||
        rateplan?.upsellPriceInfo?.totalUpsellPriceByCurrency[currency.code]
      : null;

    // Complimentary Text
    const isFullComp = rateplan.hasComplimentaryNight && rateplan.isFullyComplimentary;
    const isPartialComp = !rateplan.isFullyComplimentary && rateplan.hasComplimentaryNight;
    let compText = '';
    if (isFullComp) {
      compText = localeObj.tf_2_RoomList_Complimentary_text;
    } else if (isPartialComp) {
      compText = localeObj.tf_2_RoomList_availableComplimentaryNight_text;
    }

    // Average Rate Plan Price
    const avgRate = this._shoppingCartSvc.getAvgRatePlanPrice(rateplan, currency.code, showPriceInclusiveOfFees);
    const hasAvgRate = avgRate > 0;

    // Discounted Rate Plan Price
    const discountedPrice = this._shoppingCartSvc.getDiscountedRatePlanPrice(
      rateplan,
      currency.code,
      showPriceInclusiveOfFees,
    );
    const hasDiscountedRate = discountedPrice > 0;

    // Tax Display
    const taxDisplay = this._shoppingCartSvc.getTaxDisplay(rateplan, currency.code, currency.symbol, localeObj);

    return {
      hasUpsellPrice,
      upsellPrice,
      isFullComp,
      isPartialComp,
      compText,
      hasAvgRate,
      avgRate,
      hasDiscountedRate,
      discountedRate: hasDiscountedRate ? discountedPrice : avgRate,
      taxDisplay,
    };
  });

  // View Child
  public readonly cardRef = viewChild(CardComponent, { read: ElementRef });

  constructor(
    public readonly _storeSvc: StoreService,
    public readonly _commonSvc: CommonService,
    public readonly _elRefSvc: ElementRef,
    public readonly _shoppingCartSvc: ShoppingCartService,
    private readonly _modalSvc: ModalService,
    private readonly _sidePanelSvc: SidePanelService,
  ) {}

  public onCardClick(event: Event) {
    const clickedElement = event.target as HTMLElement;
    if (
      !this.isExpanded() &&
      this.disableCard() &&
      !this.isAddToCartInProgress() &&
      this.cardRef()?.nativeElement.contains(clickedElement)
    ) {
      this.hideItems();
    }
  }

  public openRateplanDetails(fromOutside?: boolean) {
    if (this.isMobileScreen()) {
      this._sidePanelSvc.openSidePanel(this.rateplanDetailsContentTemplate(), {}, 'rateplan-details-side-panel');
      if (fromOutside) {
        this.rateDetailsClickedWhileCollapsed.set(fromOutside);
      }
    } else {
      this._modalSvc.openModal(this.rateplanDetailsContentTemplate(), {
        title: this.data()?.name,
        size: 'default',
      });
    }
  }

  public closeRateplanDetails() {
    if (this.isMobileScreen()) {
      this._sidePanelSvc.closeSidePanel();
      if (!this.rateDetailsClickedWhileCollapsed()) {
        setTimeout(() => {
          document.body.style.overflow = 'hidden';
        }, 500);
      }
    } else {
      this._modalSvc.closeModal('global-modal');
    }
  }

  public openRateplanRoomsListMobile() {
    this._sidePanelSvc.openSidePanel(this.rateplanRoomsListContentTemplate(), {}, 'rateplan-rooms-list-side-panel');
  }

  public closeRateplanRoomsListMobile() {
    this._sidePanelSvc.closeSidePanel('rateplan-rooms-list-side-panel');
    this.onStateChanged.emit(null);
    document.body.style.overflow = '';
  }

  public viewItems() {
    if (!this.isExpanded()) {
      this.onStateChanged.emit({ index: this.index(), state: 'EXPANDED' });
    }

    if (this.isMobileScreen()) {
      this.openRateplanRoomsListMobile();
    }
  }

  public hideItems() {
    this.onStateChanged.emit(null);
  }

  public onRoomSelected(data: { room: any; bedtype: { isDefault: boolean; code: string } }) {
    this.onRoomRateSelected.emit({
      room: data.room,
      bedtype: data.bedtype,
      rate: this.data(),
    });

    if (this._sidePanelSvc.isSidePanelOpen('global-side-panel')) {
      this.closeRateplanRoomsListMobile();
    }
  }
  /**
   * triggers the calendar to open with the selected rate code
   * @param rateplan - the rateplan object
   */
  public editDates(rateplan: any) {
    this._storeSvc.selectedRateCodeForCalendar.set(rateplan.code);
    setTimeout(() => {
      this._storeSvc.showCalendar$.next();
    }, 100);
  }
}
